import type { PressEvent } from 'react-aria';

import {
    type LoadingStateHookResult,
    useEventCallback,
    useLoadingState,
} from '@gitbook/util-react-hooks';

import { LoadingIndicator } from '../Loading';
import type { ButtonProps } from './Button.types';

function getLoadingProps(
    props: Pick<ButtonProps, 'disabled' | 'leadingIcon' | 'trailingIcon' | 'onPress' | 'size'> & {
        loading: LoadingStateHookResult[0];
    }
) {
    const { disabled, leadingIcon, loading, onPress, size, trailingIcon } = props;
    const loadingIndicator = () => (
        <LoadingIndicator size={size === 'small' ? 'xsmall' : 'medium'} />
    );
    // if there's a leading icon, use that to show loading indicator
    // otherwise use the trailing icon (if there is one)
    // if neither add loading indicator as leading icon (which might cause some layout shift)
    const icons = loading
        ? {
              leadingIcon: leadingIcon
                  ? loadingIndicator
                  : trailingIcon
                    ? undefined
                    : loadingIndicator,
              trailingIcon: leadingIcon
                  ? trailingIcon
                  : trailingIcon
                    ? loadingIndicator
                    : trailingIcon,
          }
        : { leadingIcon, trailingIcon };

    return {
        leadingIcon: icons.leadingIcon,
        trailingIcon: icons.trailingIcon,
        disabled: disabled || loading,
        'data-loading': loading || undefined,
        onPress,
    };
}

export function useLoadableButton(props: ButtonProps): ButtonProps {
    const { disabled, leadingIcon, trailingIcon, size, loadable = false, onPress } = props;

    const defaultLoadingState = useLoadingState();
    const [loading, wrapLoading] = Array.isArray(loadable) ? loadable : defaultLoadingState;

    const handleOnPress = useEventCallback((event: PressEvent) => {
        const result: any = onPress?.(event);
        if (result instanceof Promise) {
            wrapLoading?.(result);
        }
    });

    if (props.loadable === false) {
        return props;
    }

    // if the button is loadable enrich the button's props with the loading state
    return {
        ...props,
        ...getLoadingProps({
            size,
            disabled,
            leadingIcon,
            trailingIcon,
            loading,
            onPress: onPress ? handleOnPress : undefined,
        }),
    };
}
